// Utilidad para generar y descargar un archivo KML a partir de posiciones históricas
// Cada posición debe contener al menos: latitude, longitude, gpsDate (ISO), locationName, speed, eventName
// Uso: generateAndDownloadKML(positions, { unitName, from, to })

interface HistoricalPositionLike {
  latitude: number;
  longitude: number;
  gpsDate?: string;
  locationName?: string;
  speed?: number;
  eventName?: string;
  eventId?: number | null;
}

interface KMLMeta {
  unitName?: string;
  from?: string; // ISO
  to?: string;   // ISO
}

const escapeXml = (value: any) =>
  (value ?? '')
    .toString()
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

export function buildKML(positions: HistoricalPositionLike[], meta: KMLMeta = {}): string {
  const safePositions = (positions || []).filter(
    (p) => p && typeof p.latitude === 'number' && typeof p.longitude === 'number'
  );

  const header = `<?xml version="1.0" encoding="UTF-8"?>\n`;
  const kmlOpen = `<kml xmlns="http://www.opengis.net/kml/2.2"><Document>`;
  const name = `<name>${escapeXml(
    `Historico ${meta.unitName || ''} ${meta.from || ''} ${meta.to || ''}`.trim()
  )}</name>`;
  const description = `<description>${escapeXml(
    `Historical positions export${meta.unitName ? ' - ' + meta.unitName : ''}`
  )}</description>`;

  // Styles simples
  const styles = `
    <Style id="lineStyle"><LineStyle><color>ff0055ff</color><width>3</width></LineStyle></Style>
    <Style id="pointStyle"><IconStyle><color>ff0055ff</color><scale>0.8</scale><Icon><href>http://maps.google.com/mapfiles/kml/shapes/placemark_circle.png</href></Icon></IconStyle></Style>
  `;

  const placemarks = safePositions
    .map((p, idx) => {
      const coords = `${p.longitude},${p.latitude},0`;
      const when = p.gpsDate ? `<TimeStamp><when>${escapeXml(p.gpsDate)}</when></TimeStamp>` : '';
      const details: Record<string, any> = {
        Location: p.locationName,
        Event: p.eventName,
        Speed: p.speed,
        Index: idx + 1
      };
      const extended = Object.entries(details)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(
          ([k, v]) => `<Data name="${escapeXml(k)}"><value>${escapeXml(v)}</value></Data>`
        )
        .join('');
      return `
        <Placemark>
          <name>${escapeXml(p.eventName || p.locationName || 'Posición ' + (idx + 1))}</name>
          ${when}
          <styleUrl>#pointStyle</styleUrl>
          <ExtendedData>${extended}</ExtendedData>
          <Point><coordinates>${coords}</coordinates></Point>
        </Placemark>`;
    })
    .join('\n');

  const lineString = safePositions.length
    ? `
      <Placemark>
        <name>Trayectoria</name>
        <styleUrl>#lineStyle</styleUrl>
        <LineString>
          <tessellate>1</tessellate>
          <coordinates>
            ${safePositions.map((p) => `${p.longitude},${p.latitude},0`).join(' ')}
          </coordinates>
        </LineString>
      </Placemark>`
    : '';

  const kmlClose = `</Document></kml>`;
  return [header, kmlOpen, name, description, styles, placemarks, lineString, kmlClose]
    .filter(Boolean)
    .join('\n');
}

export function downloadKML(kmlString: string, fileName = 'historico.kml') {
  const blob = new Blob([kmlString], { type: 'application/vnd.google-earth.kml+xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 0);
}

export function generateAndDownloadKML(
  positions: HistoricalPositionLike[],
  meta: KMLMeta = {},
  fileName?: string
) {
  const kml = buildKML(positions, meta);
  const finalName =
    fileName || `historico_${meta.unitName || 'unidad'}_${meta.from || ''}_${meta.to || ''}.kml`;
  downloadKML(kml, finalName.replace(/\s+/g, '_'));
}

export default generateAndDownloadKML;
